import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { SplitText } from 'gsap/SplitText';
// Assure-toi d'importer Barba si tu utilises un bundler (Webpack, Vite, etc.)
import barba from '@barba/core';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

// On expose GSAP globalement (utile pour le débug ou scripts inline WP)
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

// =============================================================================
// --- 1. VARIABLES D'ÉTAT (Pour le nettoyage) ---
// =============================================================================
let currentLoopTl = null;
let currentScrollTrigger = null;

// =============================================================================
// --- 2. FONCTION PRINCIPALE GSAP ---
// =============================================================================
window.initPdwGSAP = function () {
    // A. NETTOYAGE PRÉALABLE (Vital pour Barba)
    if (currentLoopTl) {
        currentLoopTl.kill();
        currentLoopTl = null;
    }
    if (currentScrollTrigger) {
        currentScrollTrigger.kill();
        currentScrollTrigger = null;
    }

    // B. SÉLECTION ET SÉCURITÉ
    const rail = document.querySelector('.scrolling-text .rail');
    if (!rail) return;

    if (rail.getAttribute('data-init') === 'true') return;

    // C. LOGIQUE D'ANIMATION
    const content = rail.innerHTML;
    rail.innerHTML += content;
    rail.setAttribute('data-init', 'true');

    const scrollingItems = gsap.utils.toArray('.scrolling-text .rail .marquee-item');
    if (!scrollingItems.length) return;

    currentLoopTl = horizontalLoop(scrollingItems, {
        repeat: -1,
        speed: 1,
        paddingRight: parseFloat(gsap.getProperty(rail, "paddingRight")) || 0
    });

    let speedTween;

    currentScrollTrigger = ScrollTrigger.create({
        trigger: ".scrolling-text",
        start: "top bottom",
        end: "bottom top",
        onUpdate: (self) => {
            if (speedTween) speedTween.kill();

            speedTween = gsap.timeline()
                .to(currentLoopTl, {
                    timeScale: 2.5 * self.direction,
                    duration: 0.1
                })
                .to(currentLoopTl, {
                    timeScale: 1 * (self.direction || 1),
                    duration: 1
                }, "+=0.2");
        },
        markers: false
    });
};

// =============================================================================
// --- 3. HELPER FUNCTION (Standard GSAP) ---
// =============================================================================
function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    let tl = gsap.timeline({
            repeat: config.repeat,
            paused: config.paused,
            defaults: { ease: "none" },
            onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
        }),
        length = items.length,
        startX = items[0].offsetLeft,
        times = [],
        widths = [],
        xPercents = [],
        curIndex = 0,
        pixelsPerSecond = (config.speed || 1) * 100,
        snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1),
        totalWidth, curX, distanceToStart, distanceToLoop, item, i;

    gsap.set(items, {
        xPercent: (i, el) => {
            let w = widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
            xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / w * 100 + gsap.getProperty(el, "xPercent"));
            return xPercents[i];
        }
    });
    gsap.set(items, { x: 0 });

    totalWidth = items[length - 1].offsetLeft + xPercents[length - 1] / 100 * widths[length - 1] - startX + items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], "scaleX") + (parseFloat(config.paddingRight) || 0);

    for (i = 0; i < length; i++) {
        item = items[i];
        curX = xPercents[i] / 100 * widths[i];
        distanceToStart = item.offsetLeft + curX - startX;
        distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");

        tl.to(item, {
            xPercent: snap((curX - distanceToLoop) / widths[i] * 100),
            duration: distanceToLoop / pixelsPerSecond
        }, 0)
            .fromTo(item, { xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100) }, {
                xPercent: xPercents[i],
                duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                immediateRender: false
            }, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);

        times[i] = distanceToStart / pixelsPerSecond;
    }

    function toIndex(index, vars) {
        vars = vars || {};
        (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length);
        let newIndex = gsap.utils.wrap(0, length, index),
            time = times[newIndex];
        if (time > tl.time() !== index > curIndex) {
            vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
            time += tl.duration() * (index > curIndex ? 1 : -1);
        }
        curIndex = newIndex;
        vars.overwrite = true;
        return tl.tweenTo(time, vars);
    }

    tl.next = vars => toIndex(curIndex + 1, vars);
    tl.previous = vars => toIndex(curIndex - 1, vars);
    tl.current = () => curIndex;
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true);

    if (config.reversed) {
        tl.vars.onReverseComplete();
        tl.reverse();
    }
    return tl;
}

// =============================================================================
// --- 4. INITIALISATION BARBA ET GRAVITY FORMS ---
// =============================================================================

// Hook Barba : Exécuté après chaque transition vers une nouvelle page
barba.hooks.after((data) => {

    // 1. On relance nos animations GSAP
    if (typeof window.initPdwGSAP === 'function') {
        window.initPdwGSAP();
    }

    // 2. On ré-évalue les scripts Gravity Forms pour qu'ils fonctionnent
    if (data.next.container) {
        const scripts = data.next.container.querySelectorAll('script');

        scripts.forEach((script) => {
            // Cible les scripts Gravity Forms
            if (script.textContent.includes('gform') || (script.src && script.src.includes('gravityforms'))) {

                const newScript = document.createElement('script');

                if (script.src) {
                    newScript.src = script.src;
                } else {
                    newScript.textContent = script.textContent;
                }

                document.body.appendChild(newScript);
                newScript.parentNode.removeChild(newScript);
            }
        });

        // Force jQuery à déclencher le rendu de Gravity Forms (vital pour la validation)
        if (window.jQuery && window.gform) {
            window.jQuery(document).trigger('gform_post_render', [1, 1]);
        }
    }
});

// Lancement au premier chargement du site
document.addEventListener("DOMContentLoaded", () => {

    // Initialisation GSAP au premier load
    window.initPdwGSAP();

    // Initialisation de Barba
    barba.init({
        // Tu peux placer tes transitions personnalisées ici
        transitions: [{
            name: 'default-transition',
            leave(data) {
                return gsap.to(data.current.container, { opacity: 0, duration: 0.5 });
            },
            enter(data) {
                return gsap.from(data.next.container, { opacity: 0, duration: 0.5 });
            }
        }]
    });
});